import './../src/storybook/theme/theme.css';
import GoogleSignInPlugin from 'vue3-google-signin';
import { camelCase } from '@replygirl/change-case-object';

import { createPinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import { createApp } from 'vue';
import { createVfm } from 'vue-final-modal';

import App from './../src/App.vue';
import createRouter from './../src/router';

import { StoreInitializer, StoreInitializerData } from './../src/storeInitializer';
import { createPusherPlugin } from './../src/plugins/pusher';
import { useSettingsStore, type PusherConfig } from './../src/store/useSettingsStore';
import { createZightTheme } from './../src/storybook/setup';

export interface CopernicusConfig {
  pusher: PusherConfig;
  mainDomain: string;
  googleClientId: string;
  appLinks: { [key: string]: string };
  returnTo: string;
}

export class Copernicus {
  public config: CopernicusConfig;

  constructor({ store, config }: { store: Partial<StoreInitializerData>; config: CopernicusConfig }) {
    const pinia = createPinia();
    pinia.use(piniaPluginPersistedstate);

    this.config = config;

    const app = createApp(App)
                  .use(pinia)
                  .use(createRouter(config.mainDomain))
                  .provide('config', config);

    const vfm = createVfm();
    app.use(vfm);

    if (store) {
      new StoreInitializer().init(camelCase(store));
    }

    // Initialize settings store after Pinia is set up
    const settingsStore = useSettingsStore();

    // Only initialize Pusher if we have a valid configuration with required fields
    const hasPusherConfig = config.pusher && config.pusher.apiKey && config.pusher.authUrl;
    if (hasPusherConfig) {
      settingsStore.setPusherConfig(config.pusher);
      app.use(createPusherPlugin, config.pusher);
    } else {
      console.warn('Pusher configuration is missing or incomplete. Real-time features will be disabled.');
    }

    if (config.googleClientId) {
      app.use(GoogleSignInPlugin, { clientId: config.googleClientId });
    }

    createZightTheme().then(theme => {
      app.use(theme);
      app.mount('#app');
    });
  }
}

Object.assign(window, { Copernicus });
